'use client';

/**
 * The notification rows and the list around them (lib/notifications.ts).
 *
 * Reddit's inbox, kept simple: a round picture of what it is about (the
 * Camp's emblem, the game, the award, the rank, the person) with a small badge
 * for the kind, the line in bold while unread, a quote of what it is about,
 * how long ago, and a dot. Used by the bell's dropdown, the
 * "Show all" side panel and Settings › Notifications.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import MarqCoin from '../MarqCoin';
import { CampIcon } from '../camps/CampArt';
import { AwardIcon, rankTone } from '../camps/bits';
import { COVER_FRAMES } from '@/lib/gameCovers';
import { fetchNotices, markRead, ago, type Notice, type NoticePic, type NotifyKind, type NotifyTab } from '@/lib/notifications';

// ── The mark for each kind ───────────────────────────────────────────────────

const TONE: Record<string, string> = {
  upvote: 'bg-orange-500', award: 'bg-amber-400', reply: 'bg-sky-500', mention: 'bg-violet-500', mod: 'bg-emerald-600',
  rank: 'bg-teal-500', kharma: 'bg-amber-500', welcome: 'bg-rose-500', site: 'bg-slate-500',
};
function Glyph({ kind }: { kind: NotifyKind }) {
  const p = { viewBox: '0 0 24 24', className: 'h-[13px] w-[13px]', fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (kind) {
    case 'upvote': return <svg {...p} fill="currentColor" stroke="none"><path d="M12 3.5l8 9h-5V21H9v-8.5H4z" /></svg>;
    case 'award': return <svg {...p} fill="currentColor" stroke="none"><path d="M12 2.5l2.9 6 6.6.8-4.9 4.5 1.3 6.5L12 17.1l-5.9 3.2 1.3-6.5L2.5 9.3l6.6-.8z" /></svg>;
    case 'reply': return <svg {...p}><path d="M4 5.5h16v10H10l-5 4v-4H4z" /></svg>;
    case 'mention': return <svg {...p}><circle cx="12" cy="12" r="3.5" /><path d="M15.5 12v1.5a2.5 2.5 0 005 0V12a8.5 8.5 0 10-3.3 6.7" /></svg>;
    case 'mod': return <svg {...p}><path d="M12 3l7.5 3v5.5c0 4.5-3.2 8-7.5 9.5-4.3-1.5-7.5-5-7.5-9.5V6z" /></svg>;
    case 'rank': return <svg {...p}><path d="M6 14l6-6 6 6M6 19l6-6 6 6" /></svg>;
    case 'welcome': return <svg {...p} fill="currentColor" stroke="none"><path d="M12 21l-1.3-1.2C5.4 15.1 2 12 2 8.2 2 5.1 4.4 2.8 7.4 2.8c1.7 0 3.4.8 4.6 2.1 1.2-1.3 2.9-2.1 4.6-2.1 3 0 5.4 2.3 5.4 5.4 0 3.8-3.4 6.9-8.7 11.6z" /></svg>;
    case 'site_user': return <svg {...p}><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0114 0" /></svg>;
    case 'site_camp': return <svg {...p}><path d="M12 4L3 20h18zM12 11l-3.5 9M12 11l3.5 9" /></svg>;
    case 'site_report': return <svg {...p}><path d="M5 21V4h12l-2.5 4L17 12H5" /></svg>;
    case 'site_held': return <svg {...p}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>;
    case 'site_feedback': return <svg {...p}><path d="M4 5h16v11H9l-5 4z" /><path d="M8.5 9.5h7M8.5 12.5h4" /></svg>;
    case 'site_award': return <svg {...p} fill="currentColor" stroke="none"><path d="M12 2.5l2.9 6 6.6.8-4.9 4.5 1.3 6.5L12 17.1l-5.9 3.2 1.3-6.5L2.5 9.3l6.6-.8z" /></svg>;
    default: return null;
  }
}

const ring = 'ring-2 ring-white dark:ring-ink-900';

/** A round image that falls back to `fallback` when it fails to load (a game's cover gone, an app's cover moved). */
function RoundImg({ src, fallback }: { src: string; fallback: React.ReactNode }) {
  const [bad, setBad] = useState(false);
  if (bad) return <>{fallback}</>;
  // eslint-disable-next-line @next/next/no-img-element -- a small, already-sized picture
  return <img src={src} alt="" loading="lazy" onError={() => setBad(true)} className="h-full w-full rounded-full object-cover ring-1 ring-black/10 dark:ring-white/10" />;
}

/** A disc in a kind's colour with its glyph: the picture when there is nothing more particular to show. */
function KindDisc({ kind }: { kind: NotifyKind }) {
  const tone = kind.startsWith('site_') ? TONE.site : TONE[kind] ?? TONE.site;
  return <span className={'grid h-full w-full place-items-center rounded-full text-white ' + tone}><span className="scale-[1.6]"><Glyph kind={kind} /></span></span>;
}

function Face({ actor, small = false }: { actor: NonNullable<Notice['actor']>; small?: boolean }) {
  if (actor.avatar) return <RoundImg src={actor.avatar} fallback={<Initial name={actor.name} small={small} />} />;
  return <Initial name={actor.name} small={small} />;
}
function Initial({ name, small }: { name: string; small: boolean }) {
  const hue = [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
  return <span className={'grid h-full w-full place-items-center rounded-full font-bold uppercase text-white ' + (small ? 'text-[10px]' : 'text-[15px]')} style={{ background: `hsl(${hue} 55% 48%)` }}>{name.slice(0, 1)}</span>;
}

/** A game with no cover: a controller on slate. */
function GameDisc() {
  return (
    <span className="grid h-full w-full place-items-center rounded-full bg-slate-600 text-white">
      <svg viewBox="0 0 24 24" className="h-1/2 w-1/2" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M7 8h10a4 4 0 014 4.5l-.6 3.3a2.3 2.3 0 01-4 1L14.6 15H9.4l-1.8 1.8a2.3 2.3 0 01-4-1L3 12.5A4 4 0 017 8z" /><path d="M8 11v3M6.5 12.5h3" /><circle cx="15.5" cy="12" r=".6" fill="currentColor" /><circle cx="17.3" cy="13.6" r=".6" fill="currentColor" />
      </svg>
    </span>
  );
}

const AWARD_BG: Record<string, string> = {
  thanks: 'bg-rose-50 ring-rose-200 dark:bg-rose-400/15 dark:ring-rose-300/30',
  great: 'bg-amber-50 ring-amber-200 dark:bg-amber-400/15 dark:ring-amber-300/30',
  legendary: 'bg-yellow-50 ring-yellow-300 dark:bg-yellow-300/15 dark:ring-yellow-200/40',
  nod: 'bg-teal-50 ring-teal-200 dark:bg-teal-400/15 dark:ring-teal-300/30',
};
function AwardDisc({ award }: { award: string }) {
  return (
    <span className={'grid h-full w-full place-items-center rounded-full ring-1 ' + (AWARD_BG[award] ?? AWARD_BG.great)}>
      {award === 'nod'
        ? <svg viewBox="0 0 24 24" className="h-[58%] w-[58%] text-teal-600 dark:text-teal-300" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.8 2.8L16.5 9.5" /></svg>
        : <AwardIcon kind={(award === 'thanks' || award === 'legendary' ? award : 'great')} className="h-[58%] w-[58%]" />}
    </span>
  );
}

/** The rank medal: the level in the rank band's colours (rose for the penalty ranks, gold at the top). */
function RankDisc({ level }: { level: number }) {
  return (
    <span className={'grid h-full w-full place-items-center rounded-full border-2 ' + rankTone(level)}>
      <span className="text-[15px] font-black tabular-nums leading-none">{level}</span>
    </span>
  );
}

/** The small badge in the corner. */
function Badge({ size, className = '', children }: { size: number; className?: string; children: React.ReactNode }) {
  return (
    <span className={'absolute -bottom-0.5 -right-0.5 grid place-items-center overflow-hidden rounded-full ' + ring + ' ' + className} style={{ width: size * 0.5, height: size * 0.5 }}>
      {children}
    </span>
  );
}
function KindBadge({ kind, size }: { kind: NotifyKind; size: number }) {
  const tone = kind.startsWith('site_') ? TONE.site : TONE[kind] ?? TONE.site;
  return <Badge size={size} className={'text-white ' + tone}><Glyph kind={kind} /></Badge>;
}
function Chevron({ down }: { down: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-[13px] w-[13px]" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={down ? 'M6 10l6 6 6-6' : 'M6 14l6-6 6 6'} />
    </svg>
  );
}

/**
 * The picture for a row, chosen by the server (routes/notifications.ts): the
 * thing the notification is about, with the kind as a badge. The badge is left
 * off where the picture already says it (a coin, an award, the Kharma mark).
 */
export function NoticeMark({ n, size = 40 }: { n: Notice; size?: number }) {
  const pic: NoticePic | null = n.pic ?? (n.actor ? { type: 'person' } : null);
  let main: React.ReactNode;
  let badge: React.ReactNode = <KindBadge kind={n.kind} size={size} />;
  switch (pic?.type) {
    case 'person':
      main = n.actor ? <Face actor={n.actor} /> : <KindDisc kind={n.kind} />;
      if (!n.actor) badge = null;
      break;
    case 'camp':
      main = <CampIcon art={pic.art} slug={pic.slug} size={size} />;
      break;
    case 'game':
      main = COVER_FRAMES[pic.id] ? <RoundImg src={`/games/covers/${pic.id}-1.webp`} fallback={<GameDisc />} /> : <GameDisc />;
      if (n.kind === 'kharma') badge = <Badge size={size}><MarqCoin className="h-full w-full" /></Badge>;
      break;
    case 'app':
      main = pic.src ? <RoundImg src={pic.src} fallback={<GameDisc />} /> : <GameDisc />;
      break;
    case 'award':
      main = <AwardDisc award={pic.award} />;
      // Who gave it, when the award is the picture.
      badge = n.actor ? <Badge size={size}><Face actor={n.actor} small /></Badge> : null;
      break;
    case 'rank':
      main = <RankDisc level={pic.level} />;
      badge = <Badge size={size} className={'text-white ' + (pic.down ? 'bg-rose-500' : 'bg-teal-500')}><Chevron down={pic.down} /></Badge>;
      break;
    case 'coin':
      main = <MarqCoin className="h-full w-full" />;
      badge = n.amount < 0 || /\btook\b/.test(n.title)
        ? <Badge size={size} className="bg-rose-500 text-[13px] font-black leading-none text-white">−</Badge>
        : <Badge size={size} className="bg-emerald-500 text-[13px] font-black leading-none text-white">+</Badge>;
      break;
    case 'brand':
      // eslint-disable-next-line @next/next/no-img-element -- the 96px Kharma mark
      main = <img src="/kharma-96.png" alt="" className="h-full w-full rounded-full" />;
      if (n.kind === 'welcome') badge = <KindBadge kind="welcome" size={size} />;
      break;
    default:
      main = <KindDisc kind={n.kind} />;
      badge = null;
  }
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      <span className="block h-full w-full">{main}</span>
      {badge}
    </span>
  );
}

// ── One row ──────────────────────────────────────────────────────────────────

export function NoticeRow({ n, onOpen, compact = false }: { n: Notice; onOpen: (n: Notice) => void; compact?: boolean }) {
  return (
    <li>
      <button type="button" onClick={() => onOpen(n)}
              className={'flex w-full items-start gap-3 px-4 text-left transition focus:outline-none focus-visible:bg-ink-100 dark:focus-visible:bg-white/[0.06] ' +
                (compact ? 'py-2.5 ' : 'py-3 ') +
                (n.read ? 'hover:bg-ink-100/70 dark:hover:bg-white/[0.04]' : 'bg-sky-50/80 hover:bg-sky-100/70 dark:bg-sky-400/[0.07] dark:hover:bg-sky-400/[0.11]')}>
        <NoticeMark n={n} size={compact ? 36 : 40} />
        <span className="min-w-0 flex-1">
          <span className={'line-clamp-2 text-[13.5px] leading-snug ' + (n.read ? 'text-ink-700 dark:text-ink-200' : 'font-semibold text-ink-900 dark:text-ink-50')}>
            {n.title}
          </span>
          {n.body && <span className="mt-0.5 line-clamp-2 block text-[12.5px] leading-snug text-ink-500 dark:text-ink-400">{n.body}</span>}
          <span className="mt-1 flex items-center gap-2 text-[11.5px] text-ink-400 dark:text-ink-500">
            <time dateTime={n.at} title={new Date(n.at).toLocaleString()}>{ago(n.at)}</time>
            {n.amount > 0 && <span className="inline-flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-300"><MarqCoin className="h-3 w-3" />+{n.amount.toLocaleString('en-US')} KHARMA</span>}
          </span>
        </span>
        {!n.read && <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-sky-500" aria-label="unread" />}
      </button>
    </li>
  );
}

// ── The list ─────────────────────────────────────────────────────────────────

export function EmptyNotices({ tab }: { tab: NotifyTab }) {
  return (
    <div className="px-6 py-10 text-center">
      <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ink-100 text-ink-400 dark:bg-white/[0.06]">
        <BellGlyph className="h-7 w-7" />
      </span>
      <p className="mt-3 text-[14px] font-semibold text-ink-800 dark:text-ink-100">{tab === 'unread' ? 'You are all caught up' : 'Nothing here yet'}</p>
      <p className="mx-auto mt-1 max-w-[16rem] text-[12.5px] text-ink-500 dark:text-ink-400">
        {tab === 'site' ? 'New accounts, Camps, reports and feedback show up here.' : 'Upvotes, awards, replies and mentions show up here.'}
      </p>
    </div>
  );
}

export function BellGlyph({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 9.5a6 6 0 0112 0c0 4.5 1.8 6.5 2.5 7.5h-17C4.2 16 6 14 6 9.5z" />
      <path d="M10 20a2 2 0 004 0" />
    </svg>
  );
}

/**
 * Tabs (All · Unread · Site for admins), "Mark all as read", the rows, and
 * more as you scroll. `onOpen` decides what clicking a row does.
 */
export function NoticeList({ admin, onOpen, initialTab = 'all', siteCount = 0, mineCount = 0 }: {
  admin: boolean; onOpen: (n: Notice) => void; initialTab?: NotifyTab; siteCount?: number; mineCount?: number;
}) {
  const [tab, setTab] = useState<NotifyTab>(initialTab);
  const [rows, setRows] = useState<Notice[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);

  const load = useCallback(async (t: NotifyTab, cursor?: string | null) => {
    setBusy(true); setErr('');
    try {
      const r = await fetchNotices(t, cursor);
      setRows((x) => (cursor && x ? [...x, ...r.notifications] : r.notifications));
      setNext(r.next);
    } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  }, []);
  useEffect(() => { setRows(null); void load(tab); }, [tab, load]);

  useEffect(() => {
    if (!next || !end.current) return;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting && !busy) void load(tab, next); }, { rootMargin: '200px' });
    io.observe(end.current);
    return () => io.disconnect();
  }, [next, busy, tab, load]);

  const open = (n: Notice) => {
    if (!n.read) { setRows((x) => x?.map((y) => (y.id === n.id ? { ...y, read: true } : y)) ?? null); void markRead({ ids: [n.id] }); }
    onOpen(n);
  };
  const allRead = async () => {
    setRows((x) => (tab === 'unread' ? [] : x?.map((y) => ({ ...y, read: true })) ?? null));
    await markRead({ tab: tab === 'site' ? 'site' : 'all' });
  };

  const tabs: { id: NotifyTab; label: string; n?: number }[] = [
    { id: 'all', label: 'All' }, { id: 'unread', label: 'Unread', n: mineCount }, ...(admin ? [{ id: 'site' as const, label: 'Site', n: siteCount }] : []),
  ];
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1 border-b border-ink-200 px-3 dark:border-white/10">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} aria-pressed={tab === t.id}
                  className={'inline-flex min-h-[42px] items-center gap-1.5 border-b-2 px-2.5 text-[13px] font-bold transition ' +
                    (tab === t.id ? 'border-accent-500 text-ink-900 dark:text-ink-50' : 'border-transparent text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100')}>
            {t.label}
            {!!t.n && <span className="rounded-full bg-rose-500 px-1.5 text-[10.5px] font-bold leading-[17px] text-white">{t.n > 99 ? '99+' : t.n}</span>}
          </button>
        ))}
        <button type="button" onClick={allRead} className="ml-auto min-h-[36px] rounded-full px-2.5 text-[12.5px] font-semibold text-accent-700 hover:bg-accent-50 dark:text-accent-300 dark:hover:bg-white/[0.06]">
          Mark all as read
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {err && <p role="alert" className="px-4 py-3 text-[13px] text-rose-600 dark:text-rose-300">{err}</p>}
        {!rows && !err && <Skeleton />}
        {rows && !rows.length && <EmptyNotices tab={tab} />}
        {rows && rows.length > 0 && <ul className="divide-y divide-ink-100 dark:divide-white/[0.06]">{rows.map((n) => <NoticeRow key={n.id} n={n} onOpen={open} />)}</ul>}
        <div ref={end} />
        {busy && rows && <p className="px-4 py-3 text-center text-[12px] text-ink-400">Loading…</p>}
      </div>
    </div>
  );
}

export function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <ul aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="flex gap-3 px-4 py-3">
          <span className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-ink-200/70 dark:bg-white/[0.07]" />
          <span className="flex-1 space-y-2 pt-1">
            <span className="block h-3 w-4/5 animate-pulse rounded bg-ink-200/70 dark:bg-white/[0.07]" />
            <span className="block h-3 w-1/2 animate-pulse rounded bg-ink-200/70 dark:bg-white/[0.07]" />
          </span>
        </li>
      ))}
    </ul>
  );
}
